import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {InputText} from "primereact/inputtext";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import PrimeDatePicker from "../../../system/components/prime-date-picker";
import {readPref, writePref} from "../../../system/ui-prefs";
import {workspaceGraphSelectors} from "../../../system/workspace-graph-slice";
import {useDeliverable} from "../../../models/deliverable/use-deliverable";
import {SETUP_STEPS, TASKS_NEEDED, isClosedProject, setupState, setupWarning} from "../../../models/project/project-setup";
import "./project-setup-dock.css";

// «داک تکمیل پروژه» (boss's task, 2026-10-24; spec «منطق داک تکمیل پروژه» and its mockup; docs/tasks/24-project-setup-dock.md)
// - only on a project's page, only for people who can edit the project, not for closed projects
// - six stations, each ticked only from the saved project (after the server answered); an inline popover above the
//   station changes it there, a «چرایی؟» popover explains why it matters with a link to the help center
// - at 100% it turns green, waits 3 s, slides away and is not shown again for that project (per user, on this device)
// - ✕ minimizes it to a badge; it stays minimized at the next visit and the next sign-in (system/ui-prefs.js)

const STATIONS = {
    owner: {icon: "person", label: "مسئول پروژه", empty: "تعیین مسئول"},
    deadline: {icon: "calendar_today", label: "تاریخ سررسید", empty: "تعیین کنید"},
    goal: {icon: "ads_click", label: "اتصال به هدف", empty: "انتخاب هدف"},
    milestone: {icon: "flag", label: "خروجی‌ها", empty: "ساخت مایلستون"},
    members: {icon: "group_add", label: "تیم پروژه", empty: "افزودن عضو"},
    tasks: {icon: "task_alt", label: "اقدامات خرد", empty: "ثبت ۳ کار اول"},
};

const WHY = {
    owner: {title: "فلسفه مالکیت در زوفینو", text: "پروژه بدون مالک، پروژه هیچ‌کس است. یک نفر باید پاسخگوی نتیجه باشد، نه فقط انجام‌دهنده کارها؛ همین شخص است که وقتی پروژه گیر می‌کند تصمیم می‌گیرد."},
    deadline: {title: "چرا پایان‌پذیری مهم است؟", text: "کاری که پایان ندارد، کش می‌آید. موعد پایان، پروژه را از «فعالیت» به «تعهد» تبدیل می‌کند و امکان سنجش عقب یا جلو بودن را می‌دهد."},
    goal: {title: "چرا نباید پروژه یتیم بماند؟", text: "پروژه بدون هدف فقط انبار فعالیت است و ظرفیت سازمان را هدر می‌دهد. اتصال به هدف نشان می‌دهد این پروژه چه چیزی را برای سازمان جلو می‌برد."},
    milestone: {title: "چرا مایلستون از تسک مهم‌تر است؟", text: "پروژه‌ای که با خالی شدن فهرست کارها تمام شود، هرگز تمام نمی‌شود! مایلستون خروجی قابل‌سنجش و نقطه پایان پروژه را تعریف می‌کند."},
    members: {title: "چرا تیم پروژه مهم است؟", text: "پروژه‌ای که فقط یک نفر در آن است کمتر دیده و پیگیری می‌شود. با افزودن تیم، مسئولیت‌ها روشن می‌شود و همکاری ممکن می‌شود."},
    tasks: {title: "تفاوت یک کار با یک ایده چیست؟", text: "تا وقتی کاری ثبت نشده، پروژه فقط یک ایده است. سه کار اول برای خروجی‌ها، اجرای پروژه را واقعاً شروع می‌کند."},
};

const WARNINGS = {
    goal: "هشدار: این پروژه یتیم است! پروژه بدون هدف فقط انبار فعالیت است. آن را به یک هدف استراتژیک وصل کنید.",
    milestone: "هشدار: پروژه شما خروجی پایان‌پذیر ندارد. برای جلوگیری از کش‌دار شدن، نقطه پایان آن را تعریف کنید.",
    tasks: "پروژه شما تعریف شده اما هیچ اقدام خردی برای استارت ندارد. حداقل ۳ کار اول را ثبت کنید.",
};

const HELP_LINK = "/help";
const fa = (value) => Number(value || 0).toLocaleString("fa-IR");
const nameOf = (membership) => membership?.member?.fullname || membership?.member?.username || "";
const keyOf = (item) => item?.documentId || item?.id;
const isOpenGoal = (goal) => goal && !goal.archivedAt && !["achieved", "canceled", "archived"].includes(goal.goalStatus);
const persianDate = (value) => (value ? new Date(value).toLocaleDateString("fa-IR", {timeZone: "Asia/Tehran", day: "numeric", month: "long"}) : "");

// ---------- popover above a station (smart placement, click outside / Esc closes) ----------
function Popover({anchor, onClose, width = 288, children}) {
    const ref = useRef(null);
    const [position, setPosition] = useState({top: -9999, left: -9999, arrow: 0, visible: false});

    useLayoutEffect(() => {
        if (!anchor || !ref.current) return;
        const rect = anchor.getBoundingClientRect();
        const height = ref.current.offsetHeight;
        let left = rect.left + rect.width / 2 - width / 2;
        left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
        const top = Math.max(12, rect.top - height - 14);
        const arrow = Math.max(16, Math.min(rect.left + rect.width / 2 - left, width - 16));
        setPosition({top, left, arrow, visible: true});
    }, [anchor, width, children]);

    useEffect(() => {
        const onDown = (event) => {
            if (ref.current?.contains(event.target) || anchor?.contains(event.target)) return;
            // menus of pickers opened from the popover live elsewhere in the page
            if (event.target.closest?.(".p-dropdown-panel, .rmdp-container, .rmdp-wrapper, .p-datepicker, [data-zofino-floating-layer]")) return;
            onClose();
        };
        const onKey = (event) => { if (event.key === "Escape") onClose(); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [anchor, onClose]);

    return createPortal(
        <div ref={ref} className={`psd-popover ${position.visible ? "is-open" : ""}`} style={{top: position.top, left: position.left, width}} dir="rtl" role="dialog">
            {children}
            <span className="psd-popover-arrow" style={{left: position.arrow}}/>
        </div>,
        document.body,
    );
}

function PopoverHead({title}) {
    return <div className="psd-popover-head">{title}</div>;
}

function HelpLink({onNavigate, text}) {
    return (
        <button type="button" className="psd-help-link" onClick={onNavigate}>
            <Icon icon="mi mi:menu_book" className="text-sm"/>{text}
        </button>
    );
}

export default function ProjectSetupDock({projectData, updateProject, refreshProject, onOpenTasks, canManage}) {
    const navigate = useNavigate();
    const userId = useSelector((state) => state.auth?.user?.id);
    const memberships = useSelector((state) => state.app.workspaceMembershipData?.workspace?.workspace_memberships);
    const workspaceId = useSelector((state) => state.app.workspaceMembershipData?.workspace?.id);
    const goals = useSelector(workspaceGraphSelectors.goals);
    const deliverableApi = useDeliverable();
    const projectKey = keyOf(projectData);
    const prefMinimized = `project-setup.minimized.${projectKey}`;
    const prefCompleted = `project-setup.completed.${projectKey}`;

    const state = useMemo(() => setupState(projectData), [projectData]);
    const [minimized, setMinimized] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [celebrating, setCelebrating] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [open, setOpen] = useState(null); // {key, kind: "action" | "why", anchor}
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const seenIncomplete = useRef(false);

    useEffect(() => {
        setMinimized(readPref(userId, prefMinimized));
        setCompleted(readPref(userId, prefCompleted));
        setCelebrating(false);
        setLeaving(false);
        setOpen(null);
        seenIncomplete.current = false;
    }, [userId, prefMinimized, prefCompleted]);

    // 100%: celebrate only when it was completed here; a project that was already complete is just remembered
    useEffect(() => {
        if (!projectKey || completed) return undefined;
        if (!state.allDone) {
            seenIncomplete.current = true;
            return undefined;
        }
        if (!seenIncomplete.current) {
            writePref(userId, prefCompleted, true);
            setCompleted(true);
            return undefined;
        }
        setOpen(null);
        setCelebrating(true);
        const slide = setTimeout(() => setLeaving(true), 3000);
        const done = setTimeout(() => {
            writePref(userId, prefCompleted, true);
            setCompleted(true);
        }, 3400);
        return () => {
            clearTimeout(slide);
            clearTimeout(done);
        };
    }, [state.allDone, completed, projectKey, userId, prefCompleted]);

    // the onboarding dock steps aside while this one is open on the page
    const showDock = Boolean(projectKey && canManage && !isClosedProject(projectData) && !completed && !minimized);
    useEffect(() => {
        document.body.classList.toggle("psd-open", showDock);
        return () => document.body.classList.remove("psd-open");
    }, [showDock]);

    if (!projectKey || !canManage || isClosedProject(projectData) || completed) return null;

    const setMin = (value) => {
        setOpen(null);
        setMinimized(value);
        writePref(userId, prefMinimized, value);
    };

    if (minimized) {
        return (
            <button type="button" className="psd-restore" onClick={() => setMin(false)}>
                <Icon icon="mi mi:checklist" className="text-lg" style={{color: "#E6511C"}}/>
                تکمیل اطلاعات پروژه
                <span className="psd-restore-count">{`${fa(state.completed)} از ${fa(state.total)}`}</span>
            </button>
        );
    }

    const toggle = (key, kind, event) => {
        event.stopPropagation();
        setError("");
        const anchor = event.currentTarget.closest(".psd-station") || event.currentTarget;
        setOpen((current) => (current && current.key === key && current.kind === kind ? null : {key, kind, anchor}));
    };
    const close = () => {
        setOpen(null);
        setError("");
    };

    // every change waits for the server; the tick comes from the project it returns
    const save = async (action) => {
        setSaving(true);
        setError("");
        try {
            const result = await action();
            if (!result) throw new Error("save-failed");
            close();
        } catch (saveError) {
            setError("ذخیره نشد. دوباره تلاش کنید.");
        } finally {
            setSaving(false);
        }
    };

    const valueOf = (key) => {
        const project = projectData;
        switch (key) {
            case "owner": return nameOf(project.owner) || STATIONS.owner.empty;
            case "deadline": return project.dueDate ? persianDate(project.dueDate) : STATIONS.deadline.empty;
            case "goal": {
                const linked = (project.goals || []).filter((goal) => !goal.archivedAt);
                if (!linked.length) return STATIONS.goal.empty;
                return linked.length > 1 ? `${linked[0].title} +${fa(linked.length - 1)}` : linked[0].title;
            }
            case "milestone": return state.done.milestone ? `${fa(state.counts.milestones)} مایلستون` : STATIONS.milestone.empty;
            case "members": return state.done.members ? `${fa(state.counts.members)} عضو` : STATIONS.members.empty;
            case "tasks": return state.done.tasks ? `${fa(state.counts.tasks)} کار ثبت شده` : state.counts.tasks ? `${fa(state.counts.tasks)} از ${fa(TASKS_NEEDED)} کار` : STATIONS.tasks.empty;
            default: return "";
        }
    };

    const warning = setupWarning(state);
    const toHelp = () => {
        close();
        navigate(HELP_LINK);
    };

    return (
        <>
            <div className={`psd-dock ${celebrating ? "is-done" : ""} ${leaving ? "is-leaving" : ""}`} dir="rtl" role="region" aria-label="تکمیل پروژه">
                {warning && !celebrating && (
                    <div className="psd-warning"><Icon icon="mi mi:warning" className="text-base"/>{WARNINGS[warning]}</div>
                )}
                {celebrating && (
                    <div className="psd-warning is-success"><Icon icon="mi mi:celebration" className="text-base"/>عالی! پروژه کامل تعریف شد و آماده اجراست.</div>
                )}
                <div className="psd-row">
                    <div className="psd-progress">
                        <div className="psd-progress-head"><span>تکمیل پروژه</span><span className="psd-percent">{`${fa(state.percent)}٪`}</span></div>
                        <div className="psd-bar"><span style={{width: `${state.percent}%`}}/></div>
                        <div className="psd-progress-status">{`${fa(state.completed)} از ${fa(state.total)} انجام شده`}</div>
                    </div>

                    <div className="psd-stations">
                        {SETUP_STEPS.map((key) => {
                            const done = state.done[key];
                            return (
                                <div key={key} className={`psd-station ${done ? "is-done" : ""} ${open?.key === key ? "is-open" : ""}`}>
                                    <button type="button" className="psd-station-main" onClick={(event) => toggle(key, "action", event)} aria-expanded={open?.key === key && open?.kind === "action"}>
                                        <span className="psd-station-icon"><Icon icon={`mi mi:${done ? "check" : STATIONS[key].icon}`} className="text-sm"/></span>
                                        <span className="psd-station-text">
                                            <span className="psd-station-label">{STATIONS[key].label}</span>
                                            <span className="psd-station-value">{valueOf(key)}</span>
                                        </span>
                                    </button>
                                    <button type="button" className="psd-why" onClick={(event) => toggle(key, "why", event)}>چرایی؟</button>
                                </div>
                            );
                        })}
                    </div>

                    <button type="button" className="psd-close" title="کوچک کردن" aria-label="کوچک کردن" onClick={() => setMin(true)}>
                        <Icon icon="mi mi:close" className="text-lg"/>
                    </button>
                </div>
            </div>

            {open?.kind === "why" && (
                <Popover anchor={open.anchor} onClose={close} width={300}>
                    <PopoverHead title={WHY[open.key].title}/>
                    <div className="p-3 flex flex-column gap-2">
                        <p className="m-0 text-xs text-700 line-height-3">{WHY[open.key].text}</p>
                        <HelpLink onNavigate={toHelp} text="مطالعه فصل پروژه‌های معنادار در مرکز راهنما"/>
                    </div>
                </Popover>
            )}

            {open?.kind === "action" && (
                <Popover anchor={open.anchor} onClose={close} width={open.key === "goal" || open.key === "members" ? 320 : 288}>
                    <StationPopover
                        stationKey={open.key}
                        projectData={projectData}
                        memberships={memberships || []}
                        goals={(goals || []).filter(isOpenGoal)}
                        state={state}
                        saving={saving}
                        error={error}
                        onHelp={toHelp}
                        onOpenTasks={() => {
                            close();
                            onOpenTasks?.();
                        }}
                        onSaveProject={(update) => save(() => updateProject(projectData, update))}
                        onCreateMilestone={(title) => save(async () => {
                            const created = await deliverableApi.createDeliverable({title, workspace: workspaceId, project: projectData.id, deliverableStatus: "todo"});
                            if (!created) return null;
                            await refreshProject?.();
                            return created;
                        })}
                    />
                </Popover>
            )}
        </>
    );
}

function StationPopover({stationKey, projectData, memberships, goals, state, saving, error, onHelp, onOpenTasks, onSaveProject, onCreateMilestone}) {
    const [date, setDate] = useState(projectData?.dueDate ? Math.floor(new Date(projectData.dueDate).getTime() / 1000) : null);
    const [title, setTitle] = useState("");
    const [filter, setFilter] = useState("");
    const [changingOwner, setChangingOwner] = useState(false);
    const ownerKey = keyOf(projectData?.owner);
    const memberKeys = new Set((projectData?.members || []).map(keyOf));
    const linkedGoals = new Set((projectData?.goals || []).map((goal) => String(goal.id)));
    const people = memberships.filter((membership) => !["removed", "restricted"].includes(membership?.memberShipStatus) && nameOf(membership));
    const matches = (text) => !filter.trim() || String(text || "").includes(filter.trim());
    const errorLine = error && <span className="text-xs" style={{color: "#b91c1c"}}>{error}</span>;
    const primary = (label, onClick, disabled = false) => (
        <button type="button" className="psd-primary" disabled={saving || disabled} onClick={onClick}>{saving ? "در حال ذخیره…" : label}</button>
    );

    switch (stationKey) {
        case "owner":
            return (
                <>
                    <PopoverHead title="مسئول پروژه"/>
                    <div className="p-3 flex flex-column align-items-center gap-2 text-center">
                        {projectData?.owner ? (
                            <>
                                <Avatar memberData={projectData.owner.member} size="large"/>
                                <b className="text-sm">{nameOf(projectData.owner)}</b>
                                <span className="text-2xs text-500">مالک و پاسخگوی پروژه</span>
                            </>
                        ) : <span className="text-xs text-600">هنوز مسئولی برای این پروژه تعیین نشده است.</span>}
                        {!changingOwner ? (
                            <button type="button" className="psd-secondary w-full" onClick={() => setChangingOwner(true)}>{projectData?.owner ? "تغییر مالک" : "تعیین مالک"}</button>
                        ) : (
                            <div className="w-full flex flex-column gap-1 text-right">
                                {people.length > 6 && <InputText value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="جستجوی عضو" className="text-xs p-2"/>}
                                <div className="psd-list">
                                    {people.filter((membership) => matches(nameOf(membership))).map((membership) => (
                                        <button type="button" key={keyOf(membership)} className="psd-list-item" disabled={saving || keyOf(membership) === ownerKey}
                                                onClick={() => onSaveProject({owner: membership.id})}>
                                            <Avatar memberData={membership.member} size="normal"/><span className="flex-1">{nameOf(membership)}</span>
                                            {keyOf(membership) === ownerKey && <span className="text-2xs text-500">مالک فعلی</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {errorLine}
                        <HelpLink onNavigate={onHelp} text="فلسفه مالکیت در زوفینو"/>
                    </div>
                </>
            );
        case "deadline":
            return (
                <>
                    <PopoverHead title="تعیین تاریخ سررسید"/>
                    <div className="p-3 flex flex-column gap-2">
                        <PrimeDatePicker type="PERSIAN_DATE" inputClassName="w-full text-sm" placeholder="تاریخ سررسید" value={date} onChange={(event) => setDate(event.value)}/>
                        {primary("تأیید تاریخ", () => onSaveProject({dueDate: new Date(date * 1000)}), !date)}
                        {errorLine}
                        <HelpLink onNavigate={onHelp} text="چرا پایان‌پذیری مهم است؟"/>
                    </div>
                </>
            );
        case "goal": {
            const options = goals.filter((goal) => !linkedGoals.has(String(goal.id)) && matches(goal.title));
            return (
                <>
                    <PopoverHead title="اتصال به هدف استراتژیک"/>
                    <div className="p-2 flex flex-column gap-2">
                        {goals.length > 6 && <InputText value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="جستجوی هدف" className="text-xs p-2"/>}
                        <div className="psd-list">
                            {options.map((goal) => (
                                <button type="button" key={goal.id} className="psd-list-item" disabled={saving} onClick={() => onSaveProject({goals: {connect: [goal.id]}})}>
                                    <Icon icon="mi mi:ads_click" style={{color: "#10b981"}}/>
                                    <span className="flex-1 flex flex-column text-right">
                                        <b className="text-xs">{goal.title}</b>
                                        {nameOf(goal.owner) && <span className="text-2xs text-500">{`مسئول: ${nameOf(goal.owner)}`}</span>}
                                    </span>
                                </button>
                            ))}
                            {!options.length && <span className="text-xs text-500 p-2">{goals.length ? "هدف دیگری برای اتصال نیست." : "هنوز هدف فعالی در فضای کاری تعریف نشده است."}</span>}
                        </div>
                        {errorLine}
                        <HelpLink onNavigate={onHelp} text="چرا نباید پروژه یتیم بماند؟"/>
                    </div>
                </>
            );
        }
        case "milestone":
            return (
                <>
                    <PopoverHead title="تعریف خروجی (مایلستون)"/>
                    <div className="p-3 flex flex-column gap-2">
                        {state.counts.milestones > 0 && <span className="text-2xs text-500">{`این پروژه ${fa(state.counts.milestones)} مایلستون دارد؛ می‌توانید یکی دیگر اضافه کنید.`}</span>}
                        <label className="text-xs font-bold text-600">عنوان خروجی قابل پذیرش</label>
                        <InputText value={title} onChange={(event) => setTitle(event.target.value)} placeholder="مثلاً: تحویل نسخه اولیه" className="text-sm p-2"
                                   onKeyDown={(event) => { if (event.key === "Enter" && title.trim()) onCreateMilestone(title.trim()); }}/>
                        {primary("ثبت خروجی", () => onCreateMilestone(title.trim()), !title.trim())}
                        {errorLine}
                        <HelpLink onNavigate={onHelp} text="چرا مایلستون از تسک مهم‌تر است؟"/>
                    </div>
                </>
            );
        case "members": {
            const candidates = people.filter((membership) => !memberKeys.has(keyOf(membership)) && keyOf(membership) !== ownerKey && matches(nameOf(membership)));
            return (
                <>
                    <PopoverHead title="افزودن تیم پروژه"/>
                    <div className="p-2 flex flex-column gap-2">
                        {state.counts.members > 0 && <span className="text-2xs text-500 px-1">{`${fa(state.counts.members)} عضو به جز مالک در پروژه است.`}</span>}
                        {people.length > 6 && <InputText value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="جستجوی عضو" className="text-xs p-2"/>}
                        <div className="psd-list">
                            {candidates.map((membership) => (
                                <div key={keyOf(membership)} className="psd-list-item">
                                    <Avatar memberData={membership.member} size="normal"/>
                                    <span className="flex-1 text-xs font-bold">{nameOf(membership)}</span>
                                    <button type="button" className="psd-chip-button" disabled={saving} onClick={() => onSaveProject({members: {connect: [membership.id]}})}>افزودن</button>
                                </div>
                            ))}
                            {!candidates.length && <span className="text-xs text-500 p-2">همه اعضای فضای کاری در این پروژه هستند.</span>}
                        </div>
                        {errorLine}
                    </div>
                </>
            );
        }
        case "tasks":
            return (
                <>
                    <PopoverHead title="شروع اجرای پروژه"/>
                    <div className="p-3 flex flex-column align-items-center gap-2 text-center">
                        <span className="psd-round-icon"><Icon icon="mi mi:format_list_bulleted_add"/></span>
                        <p className="m-0 text-xs text-600 line-height-3">برای استارت پروژه، حداقل ۳ کار (تسک) برای خروجی‌های آن تعریف کنید. هنگام ساختن کار، مایلستون آن را انتخاب کنید.</p>
                        <span className="text-xs font-bold">{`${fa(state.counts.tasks)} از ${fa(TASKS_NEEDED)} کار متصل به مایلستون`}</span>
                        {!state.done.milestone && <span className="text-2xs" style={{color: "#b45309"}}>اول یک مایلستون بسازید تا کارها به آن وصل شوند.</span>}
                        <button type="button" className="psd-primary w-full" onClick={onOpenTasks}>رفتن به کارهای پروژه</button>
                        <HelpLink onNavigate={onHelp} text="تفاوت یک کار با یک ایده چیست؟"/>
                    </div>
                </>
            );
        default:
            return null;
    }
}
